import NiceModal from '@ebay/nice-modal-react';

import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import Button from 'components/field/Button.jsx';
import Accordion from 'components/global/Accordion';
import Icon from 'components/global/Icon';
import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';
import useMediaQuery from 'hooks/useMediaQuery';

import SubscriptionAccountModal from 'pages/Settings/Payment/SubscriptionAccount/SubscriptionAccountModal';

import { Breakpoints } from 'utils/const.styles';

import { faCheck, faCheckCircle, faMinus } from '@fortawesome/pro-solid-svg-icons';

import * as S from './FeatureBreakdownModal.styled.jsx';

const FeatureTable = ({ packages, planType, featuresBreakdown, currentPackage }) => {
	const isMobile = useMediaQuery(Breakpoints.mobileLandscape);
	const user = useSelector((state) => state.user);
	const { getLocaleDateWithTime } = useLocale();
	const { t } = useTranslation();

	return (
		<S.TableSection>
			<S.ButtonContainerFlex>
				{packages.map(
					(
						{
							planName,
							monthlyPrice,
							yearlyPrice,
							pricePlaceholder,
							currency,
							monthlyFreeIntegratorOrder,
							extraOrderCost,
							upgradeButtonLabel,
							tier
						},
						index
					) => (
						<div key={index}>
							<Typography className="mb-3 mt-0" color="var(--content-3)" component="p">
								{`${planName}.packageTitle`}
							</Typography>

							<div style={{ display: 'flex' }} className="mb-3 mobileFeaturePriceView">
								<Typography variant="p2" fontWeight="bold" className="priceMobileSize">
									{pricePlaceholder
										? t(pricePlaceholder)
										: planType === 'yearly'
											? yearlyPrice
												? (yearlyPrice / 12)?.toFixed(0)
												: yearlyPrice === 0
													? 0
													: yearlyPrice?.toFixed(0)
											: monthlyPrice}{' '}
									{planName !== 'EnterprisePackage' ? t(currency) : ' '}
								</Typography>

								{planName !== 'EnterprisePackage' && (
									<Typography
										style={{ alignSelf: 'center' }}
										variant="p2"
										fontWeight="bold"
										className="priceMobileSize"
										noWrap>
										/{t('month')}
									</Typography>
								)}
							</div>
							{planName !== 'EnterprisePackage' && (
								<div className="mb-4">
									<S.Centered>
										<Typography variant="caption" fontWeight="bold" noWrap>
											{monthlyFreeIntegratorOrder} {t('freeShipment')}/
										</Typography>
										<Typography variant="caption" color="var(--content-3)" noWrap>
											upgradeAccountPage.monthlyYourOwnRates
										</Typography>
									</S.Centered>
									<S.Centered className="mt-2">
										<Typography variant="caption" fontWeight="bold" noWrap>
											{extraOrderCost} {t(currency)}/
										</Typography>
										<Typography variant="caption" color="var(--content-3)" noWrap>
											{t('extraShipment')}
										</Typography>
									</S.Centered>
								</div>
							)}
							<div style={{ flex: 1 }} />
							{
								<div className="regularButtonView mobileButtonView">
									<Button
										variant="blue"
										size={isMobile ? 'x-small' : 'medium'}
										label={
											planName === 'EnterprisePackage'
												? upgradeButtonLabel
												: !user.accountSuspended && user.planName === planName
													? 'selected'
													: currentPackage.tier < tier
														? 'upgrade'
														: 'downgradeNow'
										}
										icon={!user.accountSuspended && user.planName === planName ? faCheck : null}
										disabled={
											!user.accountSuspended &&
											(user.planName === planName ||
												(user.nextPackageName !== user.planName &&
													planName !== 'EnterprisePackage'))
										}
										disabledTooltip={
											user.planName === planName
												? null
												: user.nextPackageName !== user.planName &&
													  planName !== 'EnterprisePackage'
													? t('requestedPackageButtonTooltip', {
															currentPlanExpiryDate: getLocaleDateWithTime(
																user.currentPlanExpiryDate
															)
														})
													: null
										}
										onClick={() =>
											NiceModal.show(SubscriptionAccountModal, {
												currentPackage,
												selectedPackage: packages.find((item) => item.planName === planName),
												selectedPlanType: planType
											})
										}
										fullWidth
									/>
								</div>
							}
						</div>
					)
				)}
			</S.ButtonContainerFlex>
			{featuresBreakdown?.map(({ title, properties }, index) =>
				!isMobile ? (
					<S.Table key={index}>
						<Accordion
							style={{ display: 'table-caption' }}
							header={title}
							defaultVisible={index === 0}
							className={
								featuresBreakdown.length - 1 === index ? 'mb-4 accordionDesign' : 'accordionDesign'
							}
							key={index}>
							<table style={{ width: '100%' }}>
								<tbody style={{ width: '100%', display: 'table' }}>
									{properties.map((feature, index) => (
										<React.Fragment key={index}>
											<tr className="fullSizeTableRow">
												<td>
													<Typography fontWeight="500" noWrap>
														{feature.label}
													</Typography>
												</td>
												<td>
													{feature.free === true ? (
														<span>
															<Icon
																size="large"
																icon={faCheckCircle}
																color="var(--green)"
															/>
														</span>
													) : feature.free === false ? (
														<Icon icon={faMinus} />
													) : (
														<Typography noWrap fullWidth>
															{feature.free}
														</Typography>
													)}
												</td>
												<td>
													{feature.startUp === true ? (
														<span>
															<Icon
																size="large"
																icon={faCheckCircle}
																color="var(--green)"
															/>
														</span>
													) : feature.startUp === false ? (
														<Icon icon={faMinus} />
													) : (
														<Typography noWrap fullWidth>
															{feature.startUp}
														</Typography>
													)}
												</td>
												<td>
													{feature.scale === true ? (
														<span>
															<Icon
																size="large"
																icon={faCheckCircle}
																color="var(--green)"
															/>
														</span>
													) : feature.scale === false ? (
														<Icon icon={faMinus} />
													) : (
														<Typography noWrap fullWidth>
															{feature.scale}
														</Typography>
													)}
												</td>
												<td>
													{feature.enterprise === true ? (
														<span>
															<Icon
																size="large"
																icon={faCheckCircle}
																color="var(--green)"
															/>
														</span>
													) : feature.enterprise === false ? (
														<Icon icon={faMinus} />
													) : (
														<Typography noWrap fullWidth>
															{feature.enterprise}
														</Typography>
													)}
												</td>
											</tr>
										</React.Fragment>
									))}
								</tbody>
							</table>
						</Accordion>
					</S.Table>
				) : (
					<S.Table key={index}>
						<Accordion
							style={{ display: 'table-caption' }}
							header={title}
							defaultVisible={index === 0}
							className={
								featuresBreakdown.length - 1 === index ? 'mb-4 accordionDesign' : 'accordionDesign'
							}
							key={index}>
							<table style={{ width: '100%' }}>
								{properties.map((feature, index) => (
									<React.Fragment key={index}>
										<tr className="mobileSizeTableHeader">
											<Typography variant="p2" fontWeight="500" noWrap>
												{feature.label}
											</Typography>
										</tr>

										<tbody style={{ width: '100%', display: 'table' }}>
											<tr className="mobileSizeTableRow">
												<td style={{ width: '25%' }}>
													{feature.free === true ? (
														<span>
															<Icon
																size="large"
																icon={faCheckCircle}
																color="var(--green)"
															/>
														</span>
													) : feature.free === false ? (
														<span>
															<Icon icon={faMinus} />
														</span>
													) : (
														<Typography noWrap>{feature.free}</Typography>
													)}
												</td>
												<td style={{ width: '25%' }}>
													{feature.startUp === true ? (
														<span>
															<Icon
																size="large"
																icon={faCheckCircle}
																color="var(--green)"
															/>
														</span>
													) : feature.startUp === false ? (
														<span>
															<Icon icon={faMinus} />
														</span>
													) : (
														<Typography noWrap>{feature.startUp}</Typography>
													)}
												</td>
												<td style={{ width: '25%' }}>
													{feature.scale === true ? (
														<span>
															<Icon
																size="large"
																icon={faCheckCircle}
																color="var(--green)"
															/>
														</span>
													) : feature.scale === false ? (
														<span>
															<Icon icon={faMinus} />
														</span>
													) : (
														<Typography noWrap>{feature.scale}</Typography>
													)}
												</td>
												<td style={{ width: '25%' }}>
													{feature.enterprise === true ? (
														<span>
															<Icon
																size="large"
																icon={faCheckCircle}
																color="var(--green)"
															/>
														</span>
													) : feature.enterprise === false ? (
														<span>
															<Icon icon={faMinus} />
														</span>
													) : (
														<Typography noWrap>{feature.enterprise}</Typography>
													)}
												</td>
											</tr>
										</tbody>
									</React.Fragment>
								))}
							</table>
						</Accordion>
					</S.Table>
				)
			)}
		</S.TableSection>
	);
};

export default FeatureTable;
